import styles from "./cms-text-block.module.css";

const MAX_WIDTH_MAP = {
  small: "420px",
  medium: "560px",
  large: "720px",
  full: "100%",
};

/** Known CMS copy mistakes → readable replacements (live CMS may still have old text). */
const COPY_FIXES = {
  "Your World Your Event Start In Globe.":
    "Your world. Your event. Starts here.",
  "Your World Your Event Start In Globe":
    "Your world. Your event. Starts here.",
  "Discover, Book & Experience Unforgettable Event Across Globe.":
    "Discover, book, and experience unforgettable events across the globe.",
  "Discover, Book & Experience Unforgettable Event Across Globe":
    "Discover, book, and experience unforgettable events across the globe.",
};

function fixCopy(text) {
  if (!text) return text;
  const trimmed = String(text).trim();
  return COPY_FIXES[trimmed] || COPY_FIXES[trimmed.replace(/\.$/, "")] || text;
}

function toCssColor(value) {
  if (!value) return undefined;
  if (typeof value === "string") return value;
  if (typeof value === "object" && value.metaColor?.isValid) {
    const { r, g, b } = value.metaColor;
    return `rgb(${r}, ${g}, ${b})`;
  }
  return undefined;
}

function normalizeLine(line) {
  if (!line) return null;

  if (typeof line === "string") {
    const text = fixCopy(line.trim());
    return text ? { text } : null;
  }

  if (typeof line === "object") {
    const text = fixCopy(
      String(line.text || line.label || line.title || "").trim(),
    );
    if (!text) return null;

    return {
      text,
      fontSize: Number.isFinite(Number(line.fontSize))
        ? Number(line.fontSize)
        : null,
      fontWeight: line.fontWeight || null,
      color: toCssColor(line.color) || null,
    };
  }

  return null;
}

function normalizeLines(lines, fallbackText, splitFallbackByComma = false) {
  if (Array.isArray(lines) && lines.length > 0) {
    return lines.map(normalizeLine).filter(Boolean);
  }

  if (typeof fallbackText !== "string" || fallbackText.trim() === "") {
    return [];
  }

  const sourceLines = splitFallbackByComma
    ? fixCopy(fallbackText).split(",")
    : fixCopy(fallbackText).split(/\n+/);

  return sourceLines.map((line) => normalizeLine(line)).filter(Boolean);
}

function getLineStyle(line, fallbackColor) {
  const style = {};
  if (line?.fontSize) style["--cms-font-size"] = `${line.fontSize}px`;
  if (line?.fontWeight) style["--cms-font-weight"] = line.fontWeight;
  const color = toCssColor(line?.color) || toCssColor(fallbackColor);
  if (color) style["--cms-color"] = color;
  return style;
}

export default function CMSTextBlock({
  headingLines,
  subheadingLines,
  heading,
  subheading,
  titleTag: TitleTag = "h1",
  className = "",
  headingClassName = "",
  subheadingClassName = "",
  headingLineClassName = "",
  subheadingLineClassName = "",
  textLayout = null,
  maxWidth = "100%",
  splitHeadingFallbackByComma = false,
  defaultHeadingColor,
  defaultSubheadingColor,
  highlightLastHeadingLine = false,
  highlightHeadingLineClassName = "",
}) {
  const normalizedHeadingLines = normalizeLines(
    headingLines,
    heading,
    splitHeadingFallbackByComma,
  );
  const normalizedSubheadingLines = normalizeLines(subheadingLines, subheading);

  if (
    normalizedHeadingLines.length === 0 &&
    normalizedSubheadingLines.length === 0
  ) {
    return null;
  }

  const width =
    (textLayout?.maxWidth && MAX_WIDTH_MAP[textLayout.maxWidth]) ||
    MAX_WIDTH_MAP[maxWidth] ||
    maxWidth ||
    "100%";

  return (
    <div
      className={[styles.wrapper, className].filter(Boolean).join(" ")}
      style={{ maxWidth: width }}
      // CMS font/color vars can differ slightly across SSR/client serialization;
      // suppress so a style-only mismatch does not poison the whole page tree.
      suppressHydrationWarning
    >
      {normalizedHeadingLines.length > 0 && (
        <TitleTag
          className={[styles.heading, headingClassName]
            .filter(Boolean)
            .join(" ")}
          suppressHydrationWarning
        >
          {normalizedHeadingLines.map((line, index) => (
            <span
              key={`${line.text}-${index}`}
              className={[
                styles.headingLine,
                headingLineClassName,
                highlightLastHeadingLine &&
                index === normalizedHeadingLines.length - 1
                  ? highlightHeadingLineClassName
                  : "",
              ]
                .filter(Boolean)
                .join(" ")}
              style={getLineStyle(line, defaultHeadingColor)}
              suppressHydrationWarning
            >
              {line.text}
            </span>
          ))}
        </TitleTag>
      )}

      {normalizedSubheadingLines.length > 0 && (
        <p
          className={[styles.subheading, subheadingClassName]
            .filter(Boolean)
            .join(" ")}
          suppressHydrationWarning
        >
          {normalizedSubheadingLines.map((line, index) => (
            <span
              key={`${line.text}-${index}`}
              className={[styles.subheadingLine, subheadingLineClassName]
                .filter(Boolean)
                .join(" ")}
              style={getLineStyle(line, defaultSubheadingColor)}
              suppressHydrationWarning
            >
              {line.text}
            </span>
          ))}
        </p>
      )}
    </div>
  );
}
